import { useState } from "react"
import { useNavigate } from "react-router-dom"

import { useGetIntegrationDetails } from "@/api/useIntegrations"
import { Button, Flex, Text } from "@/atoms"
import { INTEGRATION_VENDORS } from "@/const/vendors-constants"
import { isSelfHosted } from "@/helpers/self-hosted.helper"
import { useLogger } from "@/hooks/useLogger"
import { useTranslation } from "@/hooks/useTranslation"
import { ConnectPlug } from "@/icons"
import { Protected } from "@/molecules/Protected"
import { ReadInstructionButton } from "@/molecules/ReadInstructionButton"
import { ConnectMethodSteps } from "@/pages/IntegrationsPage/components/molecules/ConnectMethodSteps"
import { INTEGRATIONS_WRITE_PERMISSION } from "@/pages/IntegrationsPage/IntegrationsPage.constants"
import { AboutSection } from "../../molecules/AboutSection"
import { PrerequisitesSection } from "../../molecules/PrerequisitesSection"
import { LinkIcon } from "../../organisms/ItemIntegration/LinkIcon"
import {
  DEFAULT_ENVIRONMENT,
  GITHUB_ENVIRONMENT_VALUES
} from "./GitHubConnectTab.constants"

import styles from "./GitHubConnectTab.module.scss"

import type { TypedOption } from "@/molecules/DescriptiveSelect"
import type { GitHubEnvironment } from "./GitHubConnectTab.types"

export const GitHubConnectTab = () => {
  const { t } = useTranslation("translation", {
    keyPrefix: "integrations.connect.github"
  })
  const { logger, EVENTS } = useLogger()
  const navigate = useNavigate()
  const { response } = useGetIntegrationDetails(INTEGRATION_VENDORS.github)
  const integration = response?.data?.integration
  const [firstSection, secondSection] = integration?.connectPage?.sections || []
  const submitButton = integration?.connectPage?.submitButton

  const [environment, setEnvironment] =
    useState<GitHubEnvironment>(DEFAULT_ENVIRONMENT)
  const isCustomDomain = environment === "githubCustomDomain"
  const customAppPath = `/integrations/${INTEGRATION_VENDORS.github}/connect`

  const environmentOptions: TypedOption<GitHubEnvironment>[] =
    GITHUB_ENVIRONMENT_VALUES.map((value) => ({
      value,
      label: t(`environment.options.${value}`),
      description: t(`environment.options.${value}Description`)
    }))

  const goToCustomApp = (loggerMeta: Record<string, unknown>) => {
    logger.info(EVENTS.ANALYTIC_EVENTS.INTEGRATIONS.OPENED_CONNECT, loggerMeta)
    navigate(customAppPath)
  }

  return (
    <>
      {firstSection && <AboutSection section={firstSection} hasDivider />}
      {secondSection && <PrerequisitesSection section={secondSection} />}
      {secondSection?.instructionsLink && (
        <Flex justify="flex-end">
          <ReadInstructionButton href={secondSection.instructionsLink} />
        </Flex>
      )}
      {isSelfHosted() ? (
        <Flex
          vertical
          align="flex-start"
          gap={8}
          className={styles.selfHostedInstall}
        >
          <Text>{t("installApp.title")}:</Text>
          <Protected permission={INTEGRATIONS_WRITE_PERMISSION} shouldDisable>
            <Button
              icon={<ConnectPlug />}
              type="primary"
              onClick={() =>
                goToCustomApp({ name: INTEGRATION_VENDORS.github })
              }
            >
              {t("installApp.connectCustom")}
            </Button>
          </Protected>
        </Flex>
      ) : (
        <ConnectMethodSteps<GitHubEnvironment>
          selectTitle={t("environment.title")}
          value={environment}
          options={environmentOptions}
          onChange={setEnvironment}
          actionTitle={t("installApp.title")}
          action={
            <Protected permission={INTEGRATIONS_WRITE_PERMISSION} shouldDisable>
              {isCustomDomain ? (
                <Button
                  className={styles.connectCustomButton}
                  icon={<ConnectPlug />}
                  onClick={() =>
                    goToCustomApp({
                      name: INTEGRATION_VENDORS.github,
                      environment
                    })
                  }
                >
                  {t("installApp.connectCustom")}
                </Button>
              ) : (
                <Button
                  icon={<LinkIcon type="externalLink" />}
                  type="primary"
                  href={submitButton?.link}
                  target="_blank"
                  onClick={() => {
                    logger.info(
                      EVENTS.ANALYTIC_EVENTS.INTEGRATIONS.OPENED_INSTRUCTION,
                      { name: INTEGRATION_VENDORS.github, environment }
                    )
                  }}
                >
                  {t("installApp.continue")}
                </Button>
              )}
            </Protected>
          }
          helper={t(`installApp.helper.${environment}`)}
        />
      )}
    </>
  )
}
